/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
    let t;
    return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
    };
}

// Auto-scroll configuration
const AUTO_SCROLL_CONFIG = {
    defaultSpeed: 3500,
    mobileBreakpoint: 599.98,
    initDelay: 400,
    initDelayLong: 600,
    initDelayExisting: 800,
    resumeDelay: 2000,
    scrollAnimationDuration: 500,
};

// Helper functions
const AutoScrollHelpers = {
    /**
     * Initialize auto-scroll properties on a carousel instance
     * @param {HTMLElement} carousel - The carousel element
     */
    initProperties(carousel) {
        if (carousel.autoScrollEnabled === undefined) {
            carousel.autoScrollEnabled = false;
            carousel.autoScrollSpeed = AUTO_SCROLL_CONFIG.defaultSpeed;
            carousel.autoScrollTimer = null;
            carousel.isPaused = false;
            carousel.manualScrollTimeout = null;
            carousel.isAutoScrolling = false;
        }
    },

    /**
     * Stop auto-scroll if method exists
     * @param {HTMLElement} carousel - The carousel element
     */
    safeStop(carousel) {
        if (typeof carousel.stopAutoScroll === "function") {
            carousel.stopAutoScroll();
        }
    },

    /**
     * Check if device is mobile
     * @returns {boolean}
     */
    isMobile() {
        const mobileMediaQuery = window.matchMedia(
            `(max-width: ${AUTO_SCROLL_CONFIG.mobileBreakpoint}px)`
        );
        let isMobile = mobileMediaQuery.matches;

        if (typeof theme !== "undefined" && theme.mediaMatches) {
            isMobile = !theme.mediaMatches.sm;
        }

        return isMobile;
    },

    /**
     * Calculate mobile total pages
     * @param {HTMLElement} carousel - The carousel element
     * @returns {number|null}
     */
    calculateMobilePages(carousel) {
        const slider = carousel.slider;
        if (!slider || !carousel.slideSpan) {
            return null;
        }

        const mobileGridWidth = slider.clientWidth;
        const mobileSlidesPerPage = Math.round(
            (mobileGridWidth + (carousel.slideGap || 0)) / carousel.slideSpan
        );
        return carousel.slides.length - mobileSlidesPerPage + 1;
    },

    /**
     * Check if auto-scroll should be enabled
     * @param {HTMLElement} carousel - The carousel element
     * @returns {boolean}
     */
    shouldEnableAutoScroll(carousel) {
        // Check data attribute
        if (carousel.dataset.autoScrollMobile !== "true") {
            return false;
        }

        // Check if slider is ready
        if (!carousel.slideSpan) {
            return false;
        }

        // Check if disabled on mobile
        if (carousel.getAttribute("disable-mobile") === "true") {
            return false;
        }

        // Check if enough slides
        if (!carousel.slides || carousel.slides.length < 2) {
            return false;
        }

        // Check if mobile
        if (!AutoScrollHelpers.isMobile()) {
            return false;
        }

        // Check if needs scrolling
        if (carousel.hasAttribute("inactive")) {
            const mobilePages =
                AutoScrollHelpers.calculateMobilePages(carousel);
            if (mobilePages === null || mobilePages < 2) {
                return false;
            }
        } else if (
            typeof carousel.totalPages !== "undefined" &&
            carousel.totalPages < 2
        ) {
            const mobilePages =
                AutoScrollHelpers.calculateMobilePages(carousel);
            if (mobilePages === null || mobilePages < 2) {
                return false;
            }
        }

        return true;
    },

    /**
     * Initialize auto-scroll with proper timing
     * @param {HTMLElement} carousel - The carousel element
     * @param {number} delay - Delay in milliseconds
     */
    initAutoScrollDelayed(carousel, delay = AUTO_SCROLL_CONFIG.initDelay) {
        setTimeout(() => {
            if (
                carousel.slideSpan &&
                carousel.slider &&
                typeof carousel.initAutoScroll === "function"
            ) {
                carousel.initAutoScroll();
            }
        }, delay);
    },
};

// Wait for DOM and main.js to load, then patch CarouselSlider
(function () {
    function patchCarouselSlider() {
        // Wait for CarouselSlider to be defined
        const checkInterval = setInterval(() => {
            const existing = customElements.get("carousel-slider");

            if (existing) {
                clearInterval(checkInterval);

                const CarouselSliderProto = existing.prototype;

                // Initialize auto-scroll properties in constructor
                const originalConstructor = CarouselSliderProto.constructor;
                if (originalConstructor) {
                    const newConstructor = function () {
                        originalConstructor.call(this);
                        AutoScrollHelpers.initProperties(this);
                    };
                    newConstructor.prototype = CarouselSliderProto;
                    CarouselSliderProto.constructor = newConstructor;
                }

                // Patch init() to initialize auto-scroll properties and call initAutoScroll
                const originalInit = CarouselSliderProto.init;
                if (originalInit) {
                    CarouselSliderProto.init = function () {
                        AutoScrollHelpers.initProperties(this);
                        originalInit.call(this);

                        if (this.dataset.autoScrollMobile === "true") {
                            AutoScrollHelpers.initAutoScrollDelayed(
                                this,
                                AUTO_SCROLL_CONFIG.initDelayLong
                            );
                        }
                    };
                }

                // Patch initSlider() to call initAutoScroll
                const originalInitSlider = CarouselSliderProto.initSlider;
                if (originalInitSlider) {
                    CarouselSliderProto.initSlider = function () {
                        originalInitSlider.call(this);
                        AutoScrollHelpers.initProperties(this);

                        if (this.dataset.autoScrollMobile === "true") {
                            AutoScrollHelpers.initAutoScrollDelayed(this);
                        }
                    };
                }

                // Patch addListeners() to include auto-scroll listeners
                const originalAddListeners = CarouselSliderProto.addListeners;
                if (originalAddListeners) {
                    CarouselSliderProto.addListeners = function () {
                        originalAddListeners.call(this);
                        if (
                            typeof this.updateAutoScrollListeners === "function"
                        ) {
                            this.updateAutoScrollListeners();
                        }
                    };
                }

                // Patch removeListeners() to remove auto-scroll listeners
                const originalRemoveListeners =
                    CarouselSliderProto.removeListeners;
                if (originalRemoveListeners) {
                    CarouselSliderProto.removeListeners = function () {
                        originalRemoveListeners.call(this);
                        if (
                            typeof this.updateAutoScrollListeners === "function"
                        ) {
                            this.updateAutoScrollListeners();
                        }
                    };
                }

                // Patch handleNavClick to pause auto-scroll
                const originalHandleNavClick =
                    CarouselSliderProto.handleNavClick;
                if (originalHandleNavClick) {
                    CarouselSliderProto.handleNavClick = function (evt) {
                        if (
                            this.autoScrollEnabled &&
                            !this.isPaused &&
                            typeof this.pauseAutoScroll === "function"
                        ) {
                            this.pauseAutoScroll();
                            clearTimeout(this.manualScrollTimeout);
                            this.manualScrollTimeout = setTimeout(() => {
                                if (
                                    !this.isPaused &&
                                    typeof this.resumeAutoScroll === "function"
                                ) {
                                    this.resumeAutoScroll();
                                }
                            }, AUTO_SCROLL_CONFIG.resumeDelay);
                        }
                        originalHandleNavClick.call(this, evt);
                    };
                }

                // Patch handleBreakpointChange to stop auto-scroll
                const originalHandleBreakpointChange =
                    CarouselSliderProto.handleBreakpointChange;
                if (originalHandleBreakpointChange) {
                    CarouselSliderProto.handleBreakpointChange = function () {
                        AutoScrollHelpers.safeStop(this);
                        originalHandleBreakpointChange.call(this);
                    };
                }

                // Add auto-scroll methods
                CarouselSliderProto.initAutoScroll = function () {
                    const autoScrollSpeed =
                        parseInt(this.dataset.autoScrollSpeed, 10) ||
                        AUTO_SCROLL_CONFIG.defaultSpeed;

                    if (!AutoScrollHelpers.shouldEnableAutoScroll(this)) {
                        AutoScrollHelpers.safeStop(this);
                        return;
                    }

                    const wasEnabled = this.autoScrollEnabled;
                    this.autoScrollEnabled = true;
                    this.autoScrollSpeed = autoScrollSpeed;

                    if (
                        wasEnabled !== this.autoScrollEnabled &&
                        typeof this.updateAutoScrollListeners === "function"
                    ) {
                        this.updateAutoScrollListeners();
                    }

                    if (this.autoScrollEnabled) {
                        if (typeof this.startAutoScroll === "function") {
                            this.startAutoScroll();
                        }
                    } else {
                        AutoScrollHelpers.safeStop(this);
                    }
                };

                CarouselSliderProto.startAutoScroll = function () {
                    if (!this.autoScrollEnabled || this.autoScrollTimer) {
                        return;
                    }

                    this.isPaused = false;
                    this.autoScrollTimer = setInterval(() => {
                        if (
                            !this.isPaused &&
                            typeof this.handleAutoScroll === "function"
                        ) {
                            this.handleAutoScroll();
                        }
                    }, this.autoScrollSpeed);
                };

                CarouselSliderProto.stopAutoScroll = function () {
                    if (this.autoScrollTimer) {
                        clearInterval(this.autoScrollTimer);
                        this.autoScrollTimer = null;
                    }
                    if (this.manualScrollTimeout) {
                        clearTimeout(this.manualScrollTimeout);
                        this.manualScrollTimeout = null;
                    }
                    this.isPaused = false;
                };

                CarouselSliderProto.pauseAutoScroll = function () {
                    if (this.autoScrollEnabled) {
                        this.isPaused = true;
                    }
                };

                CarouselSliderProto.resumeAutoScroll = function () {
                    if (this.autoScrollEnabled && this.isPaused) {
                        this.isPaused = false;
                    }
                };

                CarouselSliderProto.handleAutoScroll = function () {
                    if (!this.slider || !this.slideSpan) {
                        return;
                    }

                    const currentScrollLeft = this.slider.scrollLeft;
                    const maxScrollLeft =
                        this.slider.scrollWidth - this.slider.clientWidth;
                    const nextScrollPos = currentScrollLeft + this.slideSpan;

                    this.isAutoScrolling = true;

                    if (nextScrollPos >= maxScrollLeft) {
                        this.slider.scrollTo({ left: 0, behavior: "smooth" });
                    } else {
                        this.slider.scrollTo({
                            left: nextScrollPos,
                            behavior: "smooth",
                        });
                    }

                    setTimeout(() => {
                        this.isAutoScrolling = false;
                    }, AUTO_SCROLL_CONFIG.scrollAnimationDuration);
                };

                CarouselSliderProto.handleManualScroll = function () {
                    if (this.isAutoScrolling) return;

                    if (this.autoScrollEnabled && !this.isPaused) {
                        this.pauseAutoScroll();
                        clearTimeout(this.manualScrollTimeout);
                        this.manualScrollTimeout = setTimeout(() => {
                            if (!this.isPaused) {
                                this.resumeAutoScroll();
                            }
                        }, AUTO_SCROLL_CONFIG.resumeDelay);
                    }
                };

                CarouselSliderProto.updateAutoScrollListeners = function () {
                    if (!this.slider) return;

                    if (this.autoScrollEnabled) {
                        if (!this.autoScrollListenersAdded) {
                            this.mouseEnterHandler =
                                this.mouseEnterHandler ||
                                this.pauseAutoScroll.bind(this);
                            this.mouseLeaveHandler =
                                this.mouseLeaveHandler ||
                                this.resumeAutoScroll.bind(this);
                            this.touchStartHandler =
                                this.touchStartHandler ||
                                this.pauseAutoScroll.bind(this);
                            this.touchEndHandler =
                                this.touchEndHandler ||
                                this.resumeAutoScroll.bind(this);
                            this.manualScrollHandler =
                                this.manualScrollHandler ||
                                this.handleManualScroll.bind(this);

                            this.slider.addEventListener(
                                "mouseenter",
                                this.mouseEnterHandler
                            );
                            this.slider.addEventListener(
                                "mouseleave",
                                this.mouseLeaveHandler
                            );
                            this.slider.addEventListener(
                                "touchstart",
                                this.touchStartHandler,
                                { passive: true }
                            );
                            this.slider.addEventListener(
                                "touchend",
                                this.touchEndHandler,
                                { passive: true }
                            );
                            this.slider.addEventListener(
                                "scroll",
                                this.manualScrollHandler
                            );
                            this.autoScrollListenersAdded = true;
                        }
                    } else {
                        if (this.autoScrollListenersAdded) {
                            if (this.mouseEnterHandler) {
                                this.slider.removeEventListener(
                                    "mouseenter",
                                    this.mouseEnterHandler
                                );
                                this.slider.removeEventListener(
                                    "mouseleave",
                                    this.mouseLeaveHandler
                                );
                                this.slider.removeEventListener(
                                    "touchstart",
                                    this.touchStartHandler
                                );
                                this.slider.removeEventListener(
                                    "touchend",
                                    this.touchEndHandler
                                );
                                this.slider.removeEventListener(
                                    "scroll",
                                    this.manualScrollHandler
                                );
                            }
                            this.autoScrollListenersAdded = false;
                        }
                    }
                };

                // Patch connectedCallback to ensure auto-scroll initializes
                const originalConnectedCallback =
                    CarouselSliderProto.connectedCallback;
                const connectedCallbackWrapper = function () {
                    AutoScrollHelpers.initProperties(this);

                    if (originalConnectedCallback) {
                        originalConnectedCallback.call(this);
                    }

                    if (
                        this.dataset.autoScrollMobile === "true" &&
                        typeof this.initAutoScroll === "function"
                    ) {
                        AutoScrollHelpers.initAutoScrollDelayed(
                            this,
                            AUTO_SCROLL_CONFIG.initDelayLong
                        );
                    }
                };

                CarouselSliderProto.connectedCallback =
                    connectedCallbackWrapper;

                // Patch disconnectedCallback
                const originalDisconnectedCallback =
                    CarouselSliderProto.disconnectedCallback;
                CarouselSliderProto.disconnectedCallback = function () {
                    AutoScrollHelpers.safeStop(this);
                    if (typeof this.removeListeners === "function") {
                        this.removeListeners();
                    }
                    if (originalDisconnectedCallback) {
                        originalDisconnectedCallback.call(this);
                    }
                };

                // Initialize auto-scroll for any existing carousels that already have the attribute
                const existingCarousels = document.querySelectorAll(
                    'carousel-slider[data-auto-scroll-mobile="true"]'
                );
                existingCarousels.forEach((carousel) => {
                    AutoScrollHelpers.initProperties(carousel);
                    AutoScrollHelpers.initAutoScrollDelayed(
                        carousel,
                        AUTO_SCROLL_CONFIG.initDelayExisting
                    );
                });
            } else {
                // Keep checking every 100ms for up to 5 seconds
                const elapsed = (Date.now() - startTime) / 1000;
                if (elapsed > 5) {
                    clearInterval(checkInterval);
                }
            }
        }, 100);

        const startTime = Date.now();
    }

    // Try immediately, or wait for DOM
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", patchCarouselSlider);
    } else {
        patchCarouselSlider();
    }
})();
